import { Badge, FleetStatusBadge, QualityBar } from '../../../components/Badge';
import { Card, CardBody } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { batteryTone, FLEET_STATUS_LABELS } from '../../../lib/droneStatus';
import { formatNumber } from '@shared/format';
import type { Drone } from '@shared/types';
import type { FleetLinkState } from '../../../drone/store';
import { droneFleetStatus } from '../status';
import { LinkQualityText } from './LinkQualityText';

interface DroneCardProps {
  drone: Drone;
  entry: FleetLinkState | undefined;
  now: number;
  selected: boolean;
  groupName: string;
  groupColor: string | null;
  onSelect: () => void;
  onOperate: () => void;
}

const TONE_BY_STATUS = {
  flying: 'default',
  idle: 'default',
  offline: 'default',
  fault: 'critical',
  disabled: 'default',
} as const;

/** 总览页的无人机预览卡片：名称、唯一 ID、信号质量、电量等一眼可见的信息。 */
export function DroneCard({
  drone,
  entry,
  now,
  selected,
  groupName,
  groupColor,
  onSelect,
  onOperate,
}: DroneCardProps) {
  const telemetry = entry?.telemetry ?? null;
  const status = droneFleetStatus(drone, entry, now);
  const issues = entry?.alerts?.items ?? [];
  const worstIssue = issues[0];

  return (
    <Card
      interactive
      selected={selected}
      tone={TONE_BY_STATUS[status]}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
      onDoubleClick={onOperate}
      className="flex flex-col"
    >
      <div className="flex items-start gap-2.5 p-3">
        <span
          aria-hidden
          className="mt-0.5 size-9 shrink-0 rounded-md border border-line"
          style={{
            background: groupColor ? `${groupColor}22` : 'var(--surface-2)',
            borderColor: groupColor ?? 'var(--line)',
          }}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-semibold text-fg">{drone.name || drone.uniqueId}</span>
          </div>
          <div className="tabular mt-0.5 truncate font-mono text-[11px] text-fg-subtle" title={drone.uniqueId}>
            {drone.uniqueId}
          </div>
        </div>
        <FleetStatusBadge status={status} />
      </div>

      <CardBody className="grid grid-cols-2 gap-x-3 gap-y-2 border-t border-line pt-2.5">
        <div className="flex flex-col gap-1">
          <span className="text-[10px] text-fg-subtle">电量</span>
          {telemetry ? (
            <>
              <span
                className="tabular text-xl font-semibold leading-none"
                style={{ color: `var(--${batteryTone(telemetry.battery.percent)})` }}
              >
                {formatNumber(telemetry.battery.percent, 0)}
                <span className="ml-0.5 text-xs font-normal text-fg-muted">%</span>
              </span>
              <QualityBar
                value={telemetry.battery.percent}
                tone={batteryTone(telemetry.battery.percent)}
                label={`剩余电量 ${formatNumber(telemetry.battery.percent, 0)}%`}
              />
            </>
          ) : (
            <span className="text-xl font-semibold leading-none text-fg-subtle">--</span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-[10px] text-fg-subtle">信号质量</span>
          {telemetry ? (
            <LinkQualityText
              value={telemetry.link.quality}
              frame={telemetry.seq}
              className="text-sm font-semibold leading-none"
            />
          ) : (
            <span className="text-sm font-semibold leading-none text-fg-subtle">--</span>
          )}
          <span className="text-[10px] text-fg-subtle" title="链路延迟">
            {telemetry ? `延迟 ${formatNumber(telemetry.link.latencyMs, 0)} ms` : '延迟 --'}
          </span>
        </div>

        <div className="col-span-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-fg-muted">
          <span title="GPS 定位类型与卫星数">
            卫星 <span className="tabular font-medium text-fg">{telemetry?.gps.satellites ?? '--'}</span>
          </span>
          <span title="相对起飞点高度">
            高度 <span className="tabular font-medium text-fg">{telemetry ? `${formatNumber(telemetry.position.altM, 1)} m` : '--'}</span>
          </span>
          <span title="地速">
            地速 <span className="tabular font-medium text-fg">{telemetry ? `${formatNumber(telemetry.velocity.groundSpeedMs, 1)} m/s` : '--'}</span>
          </span>
          <span className="ml-auto truncate text-fg-subtle" title={`分组：${groupName}`}>
            {groupName}
          </span>
        </div>
      </CardBody>

      <div className="flex items-center justify-between gap-2 border-t border-line px-3 py-2">
        <div className="min-w-0">
          {worstIssue ? (
            <Badge tone={worstIssue.level === 'critical' ? 'critical' : 'caution'} solid={worstIssue.level === 'critical'}>
              {worstIssue.label}
            </Badge>
          ) : (
            <span className="text-[11px] text-fg-subtle">
              {telemetry
                ? `${(Math.max(0, now - telemetry.receivedAt) / 1000).toFixed(1)} 秒前更新`
                : FLEET_STATUS_LABELS[status]}
            </span>
          )}
        </div>
        <Button
          size="sm"
          variant={selected ? 'primary' : 'default'}
          onClick={(event) => {
            event.stopPropagation();
            onOperate();
          }}
        >
          操作
        </Button>
      </div>
    </Card>
  );
}
